import React, { useEffect, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { LensCard, LensDataset } from '~/types/leLens';

import { removeDataset } from './actions/datasetColumnEdits';

export function datasetDisplayName(name: string): string {
  return name.replace(/^card query:\s*/i, 'Card-only dataset: ');
}

export type DatasetMenuProps = {
  datasets: ReadonlyArray<LensDataset>;
  cards: ReadonlyArray<LensCard>;
  activeDatasetId: string | undefined;
  disabled?: boolean;
  onSelect: (datasetId: string) => void;
  onNew: () => void;
  onRemove: (datasetId: string) => void;
  /** Lets the panel hold Escape from closing this menu instead of the whole pane. */
  onMenuOpenChange?: (open: boolean) => void;
};

/**
 * The Data tab's dataset row: one menu button in place of the old chip
 * strip, its separate kebab, and the "Choose a dataset" caption. Lists every
 * dataset on the Lens with a tick on the current one, then "New dataset…"
 * and "Remove <name>", the removal disabled up front (with its reason)
 * rather than attempted and refused after the click.
 */
export default function DatasetMenu({
  activeDatasetId,
  cards,
  datasets,
  disabled,
  onMenuOpenChange,
  onNew,
  onRemove,
  onSelect,
}: DatasetMenuProps) {
  const tokens = lensTokens(useTheme());
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const active = datasets.find((entry) => entry.id === activeDatasetId);
  const removal = active ? removeDataset(active.id, datasets, cards) : undefined;
  const blockedReason =
    removal?.kind === 'blocked'
      ? `Still read by ${removal.blockingCards.map((card) => card.title).join(', ')}.`
      : undefined;

  useEffect(() => {
    onMenuOpenChange?.(Boolean(anchor));
    // onMenuOpenChange is a hand-off, not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor]);

  return (
    <Box>
      <Button
        aria-haspopup="menu"
        aria-label={`Dataset: ${active ? datasetDisplayName(active.name) : 'none chosen'}`}
        disabled={disabled}
        endIcon={<ExpandMoreIcon fontSize="small" />}
        onClick={(event) => setAnchor(event.currentTarget)}
        size="small"
        sx={{
          color: 'text.primary',
          fontWeight: 600,
          justifyContent: 'flex-start',
          maxWidth: '100%',
          px: 0.75,
          textTransform: 'none',
          '& .MuiButton-endIcon': { flexShrink: 0 },
        }}
        title={active ? datasetDisplayName(active.name) : undefined}
        variant="text"
      >
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {active ? datasetDisplayName(active.name) : 'Choose a dataset'}
        </Box>
      </Button>
      <Menu anchorEl={anchor} onClose={() => setAnchor(null)} open={Boolean(anchor)}>
        {datasets.map((dataset) => (
          <MenuItem
            key={dataset.id}
            onClick={() => {
              onSelect(dataset.id);
              setAnchor(null);
            }}
            selected={dataset.id === activeDatasetId}
          >
            <ListItemIcon>
              {dataset.id === activeDatasetId && <CheckIcon fontSize="small" sx={{ color: tokens.accent }} />}
            </ListItemIcon>
            <ListItemText>{datasetDisplayName(dataset.name)}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            onNew();
            setAnchor(null);
          }}
        >
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>New dataset…</ListItemText>
        </MenuItem>
        {active && (
          <MenuItem
            disabled={Boolean(blockedReason)}
            onClick={() => {
              if (!blockedReason) {
                onRemove(active.id);
              }
              setAnchor(null);
            }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={`Remove ${datasetDisplayName(active.name)}`} secondary={blockedReason} />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
